'use client'

import { Pencil24 } from '@vinted/monochrome-icons'
import { Button, Card, Cell, Divider, Icon, Image, Rating, Spacer } from '@vinted/web-ui'
import { noop } from 'lodash'

import { businessAccountFromUserDto } from '@marketplace-web/business-accounts/business-accounts-data'
import { ProfileBadges } from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { scrollToElementById } from '@marketplace-web/browser/browser-interaction-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { UserTitle } from '@marketplace-web/common-components/user-title-ui'
import { ProfileUserDto } from '@marketplace-web/profile/profile-data'

import { PROFILE_SETTINGS_URL } from '../../../constants/routes'
import { useGetHasUserStatuses } from '../../../hooks/useGetHasUserStatuses'
import UserDescription from '../InfoHeader/UserDescription'
import UserInfo from '../InfoHeader/UserInfo'
import UserStatuses from '../InfoHeader/UserStatuses'
import { calculateRating } from '../../../utils/user'

const MAX_RATING = 5

type Props = {
  user: ProfileUserDto
  showSupportFunctionality: boolean
  canSendTemplates: boolean
  screen: string
  isShadowBanned?: boolean
  isPermanentlyBanned?: boolean
  isTemporarilyBanned?: boolean
  isCompromised?: boolean
  isContentTranslationEnabled: boolean
  onTogglePoliciesModal?: () => void
  onRatingClick?: () => void
}

const UserInfoPanel = ({
  user,
  showSupportFunctionality,
  canSendTemplates,
  screen,
  isShadowBanned,
  isPermanentlyBanned,
  isTemporarilyBanned,
  isCompromised,
  isContentTranslationEnabled,
  onTogglePoliciesModal = noop,
  onRatingClick = noop,
}: Props) => {
  const breakpoints = useBreakpoint()
  const translate = useTranslate('user')
  const businessTranslate = useTranslate()
  const asset = useAsset('assets/no-photo')
  const hasStatuses = useGetHasUserStatuses(user)

  if (breakpoints.phones) return null

  const photoUrl = user.photo?.url || asset(EMPTY_USER_IMAGE_NAME)
  const rating = calculateRating(user.feedback_reputation)

  function handleRatingsClick() {
    onRatingClick()
    scrollToElementById('tabs')
  }

  function renderEditProfileAction() {
    return (
      <Button
        icon={<Icon name={Pencil24} color="greyscale-level-2" />}
        url={PROFILE_SETTINGS_URL}
        text={translate('action.edit_profile')}
        theme="muted"
        size="medium"
      />
    )
  }

  function renderHeaderContent() {
    return (
      <div>
        <button type="button" onClick={handleRatingsClick} data-testid="rating-button">
          <Rating
            aria={{
              'aria-label':
                (rating && translate('a11y.rating', { rating, max_rating: MAX_RATING })) || '',
            }}
            value={rating}
            text={translate(
              'feedback.reviews',
              { count: user.feedback_count },
              { count: user.feedback_count },
            )}
            emptyStateText={translate('feedback.no_reviews')}
          />
        </button>
      </div>
    )
  }

  return (
    <div className="sticky-panel__sticky-with-tabs">
      <Card>
        <div
          className="u-flexbox u-flex-direction-column u-padding-large profile__user-info-panel"
          data-testid="user-info-panel"
        >
          <div>
            <Image src={photoUrl} styling="circle" size="x3-large" alt="" />
          </div>
          <div className="u-flex-grow u-padding-top-medium">
            <SeparatedList
              separator={
                <>
                  <Spacer size="large" />
                  <Divider />
                  <Spacer size="large" />
                </>
              }
            >
              <Cell
                title={
                  <UserTitle
                    badgeTitle={businessTranslate('business.title')}
                    businessAccountName={businessAccountFromUserDto(user)?.name}
                    login={user.login}
                    isBusiness={user.business}
                  />
                }
                body={renderHeaderContent()}
                styling="tight"
              />
              {hasStatuses && (
                <UserStatuses
                  user={user}
                  screen={screen}
                  showSupportFunctionality={showSupportFunctionality}
                  isShadowBanned={isShadowBanned}
                  isPermanentlyBanned={isPermanentlyBanned}
                  isTemporarilyBanned={isTemporarilyBanned}
                  isCompromised={isCompromised}
                  viewingSelf
                />
              )}
              <UserInfo
                user={user}
                showSupportFunctionality={showSupportFunctionality}
                canSendTemplates={canSendTemplates}
                onTogglePoliciesModal={onTogglePoliciesModal}
                viewingSelf
              />
              <div>
                {user.about && (
                  <>
                    <UserDescription
                      viewingSelf
                      user={user}
                      isContentTranslationEnabled={isContentTranslationEnabled}
                    />
                    <Spacer size="large" />
                  </>
                )}
                {renderEditProfileAction()}
              </div>
            </SeparatedList>
          </div>
        </div>
      </Card>
      <div className="profile__user-info-panel__badges">
        <ProfileBadges />
      </div>
    </div>
  )
}

export default UserInfoPanel
